/**
 * tdesign v0.48.5
 * (c) 2022 tdesign
 * @license MIT
 */

import { h as helper } from '../_chunks/dep-9d6f3de6.js';
import _typeof from '@babel/runtime/helpers/typeof';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { h, defineComponent, toRefs, ref, computed, reactive, onMounted, onBeforeUnmount } from '@vue/composition-api';
import isFunction from 'lodash/isFunction';
import upperFirst from 'lodash/upperFirst';
import isString from 'lodash/isString';
import pick from 'lodash/pick';
import get from 'lodash/get';
import { formatRowAttributes, formatRowClassNames, formatClassNames } from './utils.js';
import { getRowFixedStyles, getColumnFixedStyles } from './hooks/useFixed.js';
import useClassName from './hooks/useClassName.js';
import TEllipsis from './ellipsis.js';
import baseTableProps from './base-table-props.js';
import { getCellKey } from './hooks/useRowspanAndColspan.js';
import useLazyLoad from '../hooks/useLazyLoad.js';
import 'lodash/isObject';
import '@babel/runtime/helpers/slicedToArray';
import 'lodash/debounce';
import '../_common/js/log/index.js';
import '../_common/js/log/log.js';
import '../_common/js/utils/getScrollbarWidth.js';
import '../_common/js/utils/helper.js';
import '@babel/runtime/helpers/toConsumableArray';
import '@babel/runtime/helpers/objectWithoutProperties';
import '../utils/dom.js';
import 'vue';
import 'raf';
import '../utils/easing.js';
import '../config-provider/useConfig.js';
import '../config-provider/context.js';
import 'lodash/merge';
import 'lodash/mergeWith';
import '../_common/js/global-config/default-config.js';
import '../_common/js/global-config/locale/zh_CN.js';
import '../config-provider/type.js';
import '../utils/render-tnode.js';
import 'lodash/camelCase';
import 'lodash/kebabCase';
import '../tooltip/index.js';
import '../tooltip/tooltip.js';
import '../tooltip/props.js';
import '../popup/props.js';
import '../popup/popup.js';
import '@popperjs/core';
import '../utils/helper.js';
import '../_common/js/utils/set-style.js';
import '../popup/container.js';
import '../config-provider/config-receiver.js';
import '../utils/mixins.js';
import '../utils/map-props.js';
import '../utils/withInstall.js';
import 'lodash/capitalize';
import './style/css.js';
import '../tooltip/type.js';
import '../_common/js/utils/observe.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var TABLE_PROPS = ["rowKey", "rowClassName", "columns", "fixedRows", "footData", "rowAttributes", "rowspanAndColspan", "scroll", "cellEmptyContent", "onCellClick", "onRowClick", "onRowDblclick", "onRowMouseover", "onRowMousedown", "onRowMouseenter", "onRowMouseleave", "onRowMouseup"];
var ROW_LISTENERS = ["click", "dblclick", "mouseover", "mousedown", "mouseenter", "mouseleave", "mouseup"];
function renderCell(params, slots, extra) {
  var col = params.col,
      row = params.row,
      rowIndex = params.rowIndex;

  if (col.colKey === "serial-number") {
    return rowIndex + 1;
  }

  if (isFunction(col.cell)) {
    return col.cell(h, params);
  }

  if (slots[col.colKey]) {
    return slots[col.colKey](params);
  }

  if (isString(col.cell) && slots[col.cell]) {
    return slots[col.cell](params);
  }

  if (isFunction(col.render)) {
    return col.render(h, _objectSpread(_objectSpread({}, params), {}, {
      type: "cell"
    }));
  }

  var r = get(row, col.colKey);
  if (![void 0, "", null].includes(r)) return r;

  if (extra !== null && extra !== void 0 && extra.cellEmptyContent) {
    return isFunction(extra.cellEmptyContent) ? extra.cellEmptyContent(h, params) : extra.cellEmptyContent;
  }

  if (slots.cellEmptyContent) return slots.cellEmptyContent(params);
  return r;
}
var TrElement = defineComponent({
  name: "TR",
  props: _objectSpread(_objectSpread({
    row: Object,
    rowIndex: Number,
    dataLength: Number,
    rowAndColFixedPosition: Map,
    skipSpansMap: Map
  }, pick(baseTableProps, TABLE_PROPS)), {}, {
    scrollType: String,
    rowHeight: Number,
    trs: Map,
    bufferSize: Number,
    isVirtual: Boolean,
    tableElm: {},
    tableContentElm: {}
  }),
  setup: function setup(props, context) {
    var _toRefs = toRefs(props),
        tableContentElm = _toRefs.tableContentElm;

    var trRef = ref(null);

    var _useClassName = useClassName(),
        tdEllipsisClass = _useClassName.tdEllipsisClass,
        tableBaseClass = _useClassName.tableBaseClass,
        tableColFixedClasses = _useClassName.tableColFixedClasses,
        tableRowFixedClasses = _useClassName.tableRowFixedClasses,
        tdAlignClasses = _useClassName.tdAlignClasses,
        tableDraggableClasses = _useClassName.tableDraggableClasses;

    var trStyles = computed(function () {
      return getRowFixedStyles(get(props.row, props.rowKey || "id"), props.rowIndex, props.dataLength, props.fixedRows, props.rowAndColFixedPosition, tableRowFixedClasses);
    });
    var trAttributes = computed(function () {
      return formatRowAttributes(props.rowAttributes, {
        row: props.row,
        rowIndex: props.rowIndex,
        type: "body"
      });
    });
    var classes = computed(function () {
      var _trStyles$value;

      var customClasses = formatRowClassNames(props.rowClassName, {
        row: props.row,
        rowIndex: props.rowIndex,
        type: "body"
      }, props.rowKey || "id");
      return [(_trStyles$value = trStyles.value) === null || _trStyles$value === void 0 ? void 0 : _trStyles$value.classes, customClasses];
    });

    var _useLazyLoad = useLazyLoad(tableContentElm, trRef, reactive(_objectSpread(_objectSpread({}, props.scroll), {}, {
      rowIndex: props.rowIndex
    }))),
        hasLazyLoadHolder = _useLazyLoad.hasLazyLoadHolder,
        tRowHeight = _useLazyLoad.tRowHeight;

    var getTrListeners = function getTrListeners(row, rowIndex) {
      var trListeners = {};
      ROW_LISTENERS.forEach(function (eventName) {
        trListeners[eventName] = function (e) {
          var _props;

          var p = {
            e: e,
            row: row,
            index: rowIndex
          };
          (_props = props["onRow".concat(upperFirst(eventName))]) === null || _props === void 0 ? void 0 : _props.call(props, p);
          context.emit("row-".concat(eventName), p);
        };
      });
      return trListeners;
    };

    onMounted(function () {
      var trs = props.trs,
          rowData = props.row,
          scrollType = props.scrollType,
          isVirtual = props.isVirtual;

      if (scrollType === "virtual") {
        if (isVirtual) {
          var $index = rowData.$index;
          trs.set($index, trRef.value);
          context.emit("onRowMounted");
        }
      }
    });
    onBeforeUnmount(function () {
      if (props.isVirtual) {
        var trs = props.trs,
            row = props.row;
        var $index = row.$index;
        trs["delete"]($index);
      }
    });
    return {
      trRef: trRef,
      tableColFixedClasses: tableColFixedClasses,
      tableDraggableClasses: tableDraggableClasses,
      tSlots: context.slots,
      tdEllipsisClass: tdEllipsisClass,
      tableBaseClass: tableBaseClass,
      tdAlignClasses: tdAlignClasses,
      trStyles: trStyles,
      classes: classes,
      trAttributes: trAttributes,
      tRowHeight: tRowHeight,
      hasLazyLoadHolder: hasLazyLoadHolder,
      getTrListeners: getTrListeners
    };
  },
  methods: {
    renderEllipsisCell: function renderEllipsisCell(h2, cellParams, params) {
      var _this = this;

      var h = this.$createElement;
      var cellNode = params.cellNode;
      var col = cellParams.col;
      var content = isFunction(col.ellipsis) ? col.ellipsis(h2, cellParams) : void 0;
      return h(TEllipsis, {
        "attrs": {
          "placement": "top",
          "attach": this.tableElm ? function () {
            return _this.tableElm;
          } : void 0,
          "tooltipContent": content && function () {
            return content;
          },
          "tooltipProps": _typeof(col.ellipsis) === "object" ? col.ellipsis : void 0
        }
      }, [cellNode]);
    },
    renderTd: function renderTd(h2, params, extra) {
      var _ref,
          _this2 = this;

      var h = this.$createElement;
      var col = params.col,
          colIndex = params.colIndex,
          rowIndex = params.rowIndex;
      var cellSpans = extra.cellSpans,
          dataLength = extra.dataLength,
          rowAndColFixedPosition = extra.rowAndColFixedPosition;
      var cellNode = renderCell(params, this.tSlots, {
        cellEmptyContent: extra.cellEmptyContent
      });
      var tdStyles = getColumnFixedStyles(col, colIndex, rowAndColFixedPosition, this.tableColFixedClasses);
      var customClasses = formatClassNames(col.className, _objectSpread(_objectSpread({}, params), {}, {
        type: "td"
      }));
      var classes = [tdStyles.classes, customClasses, (_ref = {}, _defineProperty(_ref, this.tdEllipsisClass, col.ellipsis), _defineProperty(_ref, this.tableBaseClass.tdLastRow, rowIndex + cellSpans.rowspan === dataLength), _defineProperty(_ref, this.tableBaseClass.tdFirstCol, colIndex === 0 && this.rowspanAndColspan), _defineProperty(_ref, this.tdAlignClasses[col.align], col.align && col.align !== "left"), _defineProperty(_ref, this.tableDraggableClasses.handle, col.colKey === "drag"), _ref)];

      var onClick = function onClick(e) {
        var _this2$onCellClick;

        var p = _objectSpread(_objectSpread({}, params), {}, {
          e: e
        });

        (_this2$onCellClick = _this2.onCellClick) === null || _this2$onCellClick === void 0 ? void 0 : _this2$onCellClick.call(_this2, p);

        _this2.$emit("cell-click", p);
      };

      return h("td", helper([{
        "class": classes,
        "style": tdStyles.style
      }, {
        "attrs": _objectSpread(_objectSpread({}, col.attrs), cellSpans)
      }, {
        "on": {
          "click": onClick
        }
      }]), [col.ellipsis ? this.renderEllipsisCell(h2, params, {
        cellNode: cellNode
      }) : cellNode]);
    }
  },
  render: function render(h2) {
    var _this$columns,
        _this3 = this,
        _this$trStyles;

    var h = arguments[0];
    var row = this.row,
        rowIndex = this.rowIndex,
        dataLength = this.dataLength,
        rowAndColFixedPosition = this.rowAndColFixedPosition;
    var columnVNodeList = (_this$columns = this.columns) === null || _this$columns === void 0 ? void 0 : _this$columns.map(function (col, colIndex) {
      var cellSpans = {};
      var params = {
        row: row,
        col: col,
        rowIndex: rowIndex,
        colIndex: colIndex
      };
      var spanState = null;

      if (_this3.skipSpansMap.size) {
        var _spanState, _spanState2;

        var cellKey = getCellKey(row, _this3.rowKey, col.colKey, colIndex);
        spanState = _this3.skipSpansMap.get(cellKey) || {};
        ((_spanState = spanState) === null || _spanState === void 0 ? void 0 : _spanState.rowspan) > 1 && (cellSpans.rowspan = spanState.rowspan);
        ((_spanState2 = spanState) === null || _spanState2 === void 0 ? void 0 : _spanState2.colspan) > 1 && (cellSpans.colspan = spanState.colspan);
        if (spanState.skipped) return null;
      }

      return _this3.renderTd(h2, params, {
        dataLength: dataLength,
        rowAndColFixedPosition: rowAndColFixedPosition,
        columnLength: _this3.columns.length,
        cellSpans: cellSpans,
        cellEmptyContent: _this3.cellEmptyContent
      });
    });
    var attrs = this.trAttributes || {};
    return h("tr", helper([{
      "ref": "trRef"
    }, {
      "attrs": attrs
    }, {
      "style": (_this$trStyles = this.trStyles) === null || _this$trStyles === void 0 ? void 0 : _this$trStyles.style,
      "class": this.classes
    }, {
      "on": this.getTrListeners(row, rowIndex)
    }]), [this.hasLazyLoadHolder ? [h("td", {
      "style": {
        height: "".concat(this.tRowHeight, "px"),
        border: "none"
      }
    })] : columnVNodeList]);
  }
});

export { ROW_LISTENERS, TABLE_PROPS, TrElement as default, renderCell };
//# sourceMappingURL=tr.js.map
